import type { ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { haptic } from '../utils/telegram';

interface NavItem {
  path: string;
  label: string;
  icon: ReactNode;
  activeIcon: ReactNode;
}

interface BottomNavProps {
  items: NavItem[];
}

export function BottomNav({ items }: BottomNavProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const handleClick = (path: string) => {
    if (location.pathname !== path) {
      haptic.impact('light');
      navigate(path);
    }
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 h-[76px] bg-[var(--tg-theme-bg-color)]/50 backdrop-blur-[40px] border-t border-white/12 dark:border-white/8 px-10 flex items-center justify-around z-40 pb-safe shadow-[0_-8px_32px_rgba(0,0,0,0.06)] before:absolute before:inset-0 before:bg-gradient-to-t before:from-[var(--tg-theme-bg-color)]/20 before:to-transparent before:pointer-events-none">
      {items.map((item) => {
        const isActive = location.pathname === item.path;

        return (
          <button
            key={item.path}
            className={`relative flex flex-col items-center gap-1 transition-all duration-500 ease-out active:scale-95 ${
              isActive ? 'text-[var(--tg-theme-link-color)]' : 'text-[var(--tg-theme-hint-color)]'
            }`}
            onClick={() => handleClick(item.path)}
            aria-label={item.label}
            aria-current={isActive ? 'page' : undefined}
          >
            <div className={`relative p-2.5 rounded-[14px] transition-all duration-500 ease-out ${
              isActive
                ? 'bg-gradient-to-br from-[var(--tg-theme-link-color)]/20 via-[var(--tg-theme-link-color)]/15 to-[var(--tg-theme-link-color)]/10 backdrop-blur-xl shadow-[0_4px_16px_rgba(var(--tg-theme-link-color-rgb,64,150,255),0.25),0_0_0_1px_rgba(var(--tg-theme-link-color-rgb,64,150,255),0.3)] scale-110'
                : 'bg-transparent hover:bg-white/8 dark:hover:bg-white/6 hover:scale-105'
            }`}>
              {/* Active glow effect */}
              {isActive && (
                <>
                  <div className="absolute inset-0 rounded-[14px] bg-gradient-to-br from-[var(--tg-theme-link-color)]/20 via-transparent to-transparent animate-pulse" />
                  <div className="absolute -inset-[1px] rounded-[14px] bg-gradient-to-br from-[var(--tg-theme-link-color)]/30 to-transparent blur-sm" />
                </>
              )}

              {/* Icon */}
              <div className="relative z-10">
                {isActive ? item.activeIcon : item.icon}
              </div>
            </div>

            {/* Label */}
            <span className={`text-[8px] font-black uppercase tracking-[0.12em] transition-all duration-300 ${
              isActive ? 'opacity-100 translate-y-0' : 'opacity-50 translate-y-0.5'
            }`}>
              {item.label}
            </span>
          </button>
        );
      })}
    </nav>
  );
}
